import { useEffect, useState } from 'react';
import { submitFeedback } from '../api/booking';
import { useT } from '../hooks/useT';
import type { FeedbackCategory, FeedbackMode } from '../types/dtos';
import { buildConsumerFeedbackContext } from '../feedback/context';

/**
 * Consumer booking-form feedback entry point (ISSUE-0039). Renders nothing when the effective
 * consumer feedback mode is Off. Shows a technical/business picker in ChooseInFeedbackForm mode.
 */
export function FeedbackWidget({
  token,
  adviceJobId,
  mode,
}: {
  token: string;
  adviceJobId: string;
  mode: FeedbackMode | undefined;
}) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<FeedbackCategory | ''>('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!mode || mode === 'Off') return null;

  const choose = mode === 'ChooseInFeedbackForm';
  const canSubmit =
    subject.trim().length > 0 && description.trim().length > 0 && (!choose || category !== '') && !submitting;

  function close() {
    setOpen(false);
    setSubject('');
    setDescription('');
    setCategory('');
    setDone(false);
    setError(null);
  }

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitFeedback(token, {
        subject: subject.trim(),
        description: description.trim(),
        category: choose && category !== '' ? category : null,
        context: buildConsumerFeedbackContext(adviceJobId),
      });
      setDone(true);
    } catch {
      setError(t('feedback.error'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mt-6 text-center">
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-sm font-medium text-brand underline underline-offset-4 hover:text-brand-hover"
      >
        {t('feedback.button')}
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('feedback.title')}
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) close();
          }}
        >
          <div className="w-full max-w-md rounded-lg bg-white p-6 text-left shadow-xl ring-1 ring-slate-200">
            <h2 className="mb-4 text-lg font-semibold text-slate-900">{t('feedback.title')}</h2>
            {done ? (
              <div className="space-y-4">
                <p className="text-sm text-slate-700">{t('feedback.thanks')}</p>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={close}
                    className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-hover"
                  >
                    {t('feedback.close')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-slate-700">{t('feedback.subject')}</span>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    maxLength={200}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    aria-label={t('feedback.subject')}
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-sm font-medium text-slate-700">{t('feedback.description')}</span>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={5}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    aria-label={t('feedback.description')}
                  />
                </label>
                {choose && (
                  <fieldset className="space-y-1">
                    <legend className="mb-1 text-sm font-medium text-slate-700">{t('feedback.category')}</legend>
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="radio"
                        name="consumer-feedback-category"
                        checked={category === 'technical'}
                        onChange={() => setCategory('technical')}
                      />
                      {t('feedback.category_technical')}
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="radio"
                        name="consumer-feedback-category"
                        checked={category === 'business'}
                        onChange={() => setCategory('business')}
                      />
                      {t('feedback.category_business')}
                    </label>
                  </fieldset>
                )}
                {error && <p className="text-sm text-red-600">{error}</p>}
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={close}
                    className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    {t('feedback.cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-hover disabled:opacity-50"
                  >
                    {t('feedback.submit')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
